import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../environment/environment';
import {
  buildCprngFeaturesPayload,
  CPRNG_FEATURES_KEY,
  CPRNG_FEATURE_DEFS,
  CprngFeatureEntry,
  defaultCprngFeatureEntry,
  normalizeCprngFeaturesFromOrgData,
} from '../../../constants/cprng-feature-keys';
import { OrgDataJsonEditorComponent } from './org-data-json-editor.component';

export interface OrganizationListRow {
  id: number;
  org_name: string;
  data?: Record<string, unknown>;
}

export interface OrganizationLocationRow {
  id: number;
  guid: string;
  locations_name: string;
  clean_name: string | null;
}

@Component({
  selector: 'app-duna-cprng-orgsettings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatProgressBarModule,
    OrgDataJsonEditorComponent,
  ],
  templateUrl: './duna-cprng-orgsettings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './duna-cprng-orgsettings.component.scss',
})
export class DunaCprngOrgsettingsComponent implements OnInit {
  readonly featureDefs = CPRNG_FEATURE_DEFS;
  readonly cprngFeaturesKey = CPRNG_FEATURES_KEY;

  loadingOrgs = false;
  loadingDetail = false;
  saving = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  organizations: OrganizationListRow[] = [];
  filteredOrgs: OrganizationListRow[] = [];
  orgSearch = '';

  selectedOrgId: number | null = null;
  selectedOrgName = '';
  locations: OrganizationLocationRow[] = [];

  featureState: Record<string, CprngFeatureEntry> = this.emptyFeatureState();

  readonly cprngFeaturesExcludedHint =
    'Nøglen «cprng_features» redigeres i sektionen CPRNG-features ovenfor.';

  orgData: Record<string, unknown> = {};

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadOrganizations();
  }

  loadOrganizations(): void {
    this.loadingOrgs = true;
    this.clearMessages();
    this.http
      .get<OrganizationListRow[]>(`${environment.apiUrl}/organizations`, {
        withCredentials: true,
      })
      .pipe(
        finalize(() => {
          this.loadingOrgs = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (rows) => {
          this.organizations = [...(rows ?? [])].sort((a, b) =>
            a.org_name.localeCompare(b.org_name, 'da')
          );
          this.filterOrgs();
        },
        error: () => {
          this.errorMessage = 'Kunne ikke hente organisationer.';
        },
      });
  }

  filterOrgs(): void {
    const q = this.orgSearch.trim().toLowerCase();
    if (!q) {
      this.filteredOrgs = this.organizations;
    } else {
      this.filteredOrgs = this.organizations.filter(
        (o) =>
          o.org_name.toLowerCase().includes(q) ||
          String(o.id).includes(q)
      );
    }
    this.cdr.markForCheck();
  }

  selectOrg(org: OrganizationListRow): void {
    this.selectedOrgId = org.id;
    this.selectedOrgName = org.org_name;
    this.loadOrgDetail(org.id);
  }

  loadOrgDetail(orgId: number): void {
    this.loadingDetail = true;
    this.clearMessages();
    this.http
      .get<OrganizationListRow>(`${environment.apiUrl}/organizations/${orgId}`, {
        withCredentials: true,
      })
      .subscribe({
        next: (org) => {
          this.selectedOrgName = org.org_name ?? this.selectedOrgName;
          this.orgData = (org.data && typeof org.data === 'object'
            ? org.data
            : {}) as Record<string, unknown>;
          this.featureState = normalizeCprngFeaturesFromOrgData(this.orgData);
          this.cdr.markForCheck();
          this.loadLocations(orgId);
        },
        error: () => {
          this.loadingDetail = false;
          this.errorMessage = 'Kunne ikke hente organisation.';
          this.cdr.markForCheck();
        },
      });
  }

  private loadLocations(orgId: number): void {
    this.http
      .get<OrganizationLocationRow[]>(
        `${environment.apiUrl}/organizations/${orgId}/locations`,
        { withCredentials: true }
      )
      .pipe(
        finalize(() => {
          this.loadingDetail = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (locs) => {
          this.locations = locs ?? [];
        },
        error: () => {
          this.locations = [];
          this.errorMessage =
            'Kunne ikke hente lokationer (kræver superadmin).';
        },
      });
  }

  onOrgEnabledChange(featureKey: string, enabled: boolean): void {
    const entry = this.featureState[featureKey] ?? defaultCprngFeatureEntry();
    entry.org_enabled = enabled;
    this.featureState[featureKey] = entry;
    this.mergeFeaturesIntoOrgData();
    this.cdr.markForCheck();
  }

  get dataForJsonEditor(): Record<string, unknown> {
    const copy = { ...this.orgData };
    delete copy[CPRNG_FEATURES_KEY];
    return copy;
  }

  onOrgDataEditorChange(next: Record<string, unknown>): void {
    this.orgData = {
      ...next,
      [CPRNG_FEATURES_KEY]: buildCprngFeaturesPayload(this.featureState),
    };
    this.cdr.markForCheck();
  }

  saveFeatures(): void {
    if (this.selectedOrgId == null) {
      return;
    }
    this.mergeFeaturesIntoOrgData();
    const payload = buildCprngFeaturesPayload(this.featureState);
    this.saveCprngFeatures(payload);
  }

  saveOrgData(): void {
    if (this.selectedOrgId == null) {
      return;
    }
    this.mergeFeaturesIntoOrgData();
    const payload = { ...this.orgData };
    this.saving = true;
    this.clearMessages();
    this.http
      .put<OrganizationListRow>(
        `${environment.apiUrl}/organizations/${this.selectedOrgId}/data`,
        { data: payload },
        { withCredentials: true }
      )
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (org) => {
          this.orgData = (org.data ?? payload) as Record<string, unknown>;
          this.featureState = normalizeCprngFeaturesFromOrgData(this.orgData);
          this.successMessage = 'Org data er gemt.';
        },
        error: () => {
          this.errorMessage = 'Kunne ikke gemme org data.';
        },
      });
  }

  resetOrgDataFromServer(): void {
    if (this.selectedOrgId != null) {
      this.loadOrgDetail(this.selectedOrgId);
    }
  }

  private saveCprngFeatures(
    features: ReturnType<typeof buildCprngFeaturesPayload>
  ): void {
    if (this.selectedOrgId == null) {
      return;
    }
    this.saving = true;
    this.clearMessages();
    this.http
      .patch<OrganizationListRow>(
        `${environment.apiUrl}/organizations/${this.selectedOrgId}/cprng-features`,
        { [CPRNG_FEATURES_KEY]: features },
        { withCredentials: true }
      )
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (org) => {
          this.orgData = (org.data ?? this.orgData) as Record<string, unknown>;
          this.featureState = normalizeCprngFeaturesFromOrgData(this.orgData);
          this.successMessage = 'Features gemt.';
        },
        error: () => {
          this.errorMessage =
            'Kunne ikke gemme features (kræver superadmin og deployet API).';
        },
      });
  }

  private mergeFeaturesIntoOrgData(): void {
    this.orgData = {
      ...this.orgData,
      [CPRNG_FEATURES_KEY]: buildCprngFeaturesPayload(this.featureState),
    };
  }

  private emptyFeatureState(): Record<string, CprngFeatureEntry> {
    const state: Record<string, CprngFeatureEntry> = {};
    for (const def of CPRNG_FEATURE_DEFS) {
      state[def.key] = defaultCprngFeatureEntry();
    }
    return state;
  }

  private clearMessages(): void {
    this.errorMessage = null;
    this.successMessage = null;
  }
}
